import React, { useState, useEffect } from 'react';
import { ICourse } from '@cap/share/src/types/course';
import { scheduleLiveBroadcast, checkAppointment } from '@cap/share/src/apis/home';
import { loginNewWrapper, lx } from '@cap/rzmt-core';
import { message } from '@ss/mtd-react3';
import { navigatePage } from '@cap/share/src/utils/navigation';
import { useAppSelector } from '@/store/hooks';
import { selectCurModuleContentData, homeDataLoadStatus } from '@/pages/home/homeCourseData';
import { EnumModuleCode } from '@/utils/const';
import { LoadStatus } from '@/components/LoadPlaceholder';
import { ENU_COURSE_TYPE } from '@/utils/course';
import './index.less';
import { getLiveStatus, formatTimestamp, getLiveStatusImg, updateCourseListStatus } from '@/utils/func';

// 动态加载图片函数
const getImagePath = (imageName: string) => {
  try {
    // eslint-disable-next-line import/no-dynamic-require
    return require(`../../../../assets/icons/${imageName}.png`);
  } catch (e) {
    console.error('Image loading failed:', e);
    return ''; // 返回默认图片路径
  }
};
interface ItemProps extends ICourse {
  setCourseImg: React.Dispatch<React.SetStateAction<string>>;
}

const PopularLiveStreaming: React.FC = () => {
  const popularLiveClick = () => {
    lx.moduleClick({
      bid: 'b_donation_dixotcn4_mc',
    });
  };
  const pularLiveData = useAppSelector(selectCurModuleContentData);

  const loadStatus = useAppSelector(homeDataLoadStatus);

  const [dataList, setDataList] = useState(() => {
    const targetData = pularLiveData.find((c) => c.moduleCode === EnumModuleCode.Live);
    return (
      targetData || {
        page: { pageNo: 1, pageSize: 10, totalCount: 0, totalPageCount: 0 },
        courseList: [],
        moduleId: 0,
        moduleCode: EnumModuleCode.Live,
      }
    );
  });

  // 直播封面图片状态
  const [courseImg, setCourseImg] = useState<string>('');
  const [courseId, setCourseID] = useState<number>(0);

  useEffect(() => {
    const targetData = pularLiveData.find((c) => c.moduleCode === EnumModuleCode.Live);
    const liveCoverUrl = targetData?.courseList?.[0]?.liveCoverUrl || '';
    const id = targetData?.courseList?.[0]?.id || 0;
    setCourseImg(liveCoverUrl);
    setCourseID(id);
    // 设置默认数据
    const defaultData = {
      page: { pageNo: 1, pageSize: 10, totalCount: 0, totalPageCount: 0 },
      courseList: [],
      moduleId: 0,
      moduleCode: EnumModuleCode.Live,
    };

    setDataList(targetData || defaultData);

    // 提取获取用户信息和更新提醒的异步操作
    const fetchRemindData = async () => {
      const user = await loginNewWrapper.getUserInfo();
      if (user?.userId) {
        if (targetData?.courseList && targetData?.courseList.length > 0) {
          // eslint-disable-next-line no-unsafe-optional-chaining
          const courseIds = (targetData?.courseList).filter((c) => c.liveStatus !== 2).map((c) => c.id) || [];
          if (courseIds.length === 0) return;
          const { data } = await checkAppointment(courseIds);
          setDataList((prevData) => {
            const updatedCourses = updateCourseListStatus(prevData.courseList, data);

            return {
              ...prevData,
              courseList: updatedCourses,
            };
          });
        }
      }
      // 更新课程封面
    };

    fetchRemindData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pularLiveData]);

  // 鼠标悬停事件处理
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getMouseOverItem = (value: string, id: number, setCourseImg) => {
    setCourseImg(value);
    setCourseID(id);
  };

  const scheduleLiveBroadcastClick = async (event, item) => {
    const { shortUrl, type, id, liveCourseLessonId, isSetRemind, liveStatus } = item;
    const url = `/i/college/${shortUrl}/${ENU_COURSE_TYPE[type]}/${id}${
      type === 5 ? `?chapterId=${liveCourseLessonId || ''}` : ''
    }`;

    if (event.target.className === 'button') {
      if (liveStatus === 2 || liveStatus === 0) {
        navigatePage(url, {
          windowOpen: true,
        });
        return;
      }
      const user = await loginNewWrapper.getUserInfo();

      if (user?.userId) {
        try {
          await scheduleLiveBroadcast(item);
          if (!isSetRemind) {
            message.success({ message: '直播课程已经加入您的课程表，直播前10分钟也会微信或短信通知' });
          } else {
            message.success({ message: '取消课前提醒' });
          }
          const courseIds = dataList.courseList
            .filter((c) => c.liveStatus !== 2) // 直接过滤掉结束的课程
            .map((c) => c.id); // 提取 id

          if (courseIds.length === 0) return;
          const { data } = await checkAppointment(courseIds);

          setDataList((prevData) => {
            const updatedCourses = updateCourseListStatus(prevData.courseList, data);

            return {
              ...prevData,
              courseList: updatedCourses,
            };
          });
        } catch (error) {
          message.error({ message: '操作失败请重试' });
        }
      } else {
        // 未登录 去登录
        loginNewWrapper.goLogin();
      }
    } else {
      navigatePage(url, {
        windowOpen: true,
      });
    }
  };

  // 直播项组件
  const Item: React.FC<ItemProps> = ({
    title,
    liveCourseLessonStartTime,
    liveCoverUrl,
    setCourseImg: updateCourseImg,
    tenantId,
    isSetRemind,
    id,
    shortUrl,
    liveStatus,
    liveCourseLessonId,
    type,
  }) => {
    // 提前计算需要用到的数据
    const liveStatusImg = getLiveStatusImg(liveStatus, isSetRemind);
    const liveStatusText = getLiveStatus(isSetRemind, true, liveStatus);
    const liveStatusButton = getLiveStatus(isSetRemind, false, liveStatus);
    const formattedStartTime = formatTimestamp(liveCourseLessonStartTime);
    const gif = 'https://img.meituan.net/mtdxweb/b4a328bd95c9ce8d2b3c9a08bd6ef3b8152712.gif';
    const imagePath = liveStatusImg === 'livestreaming' ? gif : getImagePath(liveStatusImg);

    return (
      <div className="items" onMouseEnter={() => getMouseOverItem(liveCoverUrl, id, updateCourseImg)}>
        {/* //  */}
        <img src={imagePath} alt="Live Course Cover" />
        <div
          className="text"
          onClick={(event) => {
            scheduleLiveBroadcastClick(event, {
              id,
              shortUrl,
              tenantId,
              isSetRemind,
              liveStatus,
              type,
              liveCourseLessonId,
            });
          }}
        >
          <span className="title">{title}</span>
          <div>
            <span className="time">{formattedStartTime}</span>
            <span>{liveStatusText}</span>
          </div>
          <div
            className="button"
            onClick={(event) => {
              event.stopPropagation();
              scheduleLiveBroadcastClick(event, {
                id,
                shortUrl,
                tenantId,
                isSetRemind,
                liveStatus,
                type,
                liveCourseLessonId,
              });
            }}
          >
            {liveStatusButton}
          </div>
        </div>
      </div>
    );
  };

  const setCourseImgClick = () => {
    const item = dataList.courseList.find((c) => c.id === courseId)!;
    const { shortUrl, type, id, liveCourseLessonId } = item;
    const url = `/i/college/${shortUrl}/${ENU_COURSE_TYPE[type]}/${id}${
      type === 5 ? `?chapterId=${liveCourseLessonId || ''}` : ''
    }`;
    navigatePage(url, {
      windowOpen: true,
    });
  };

  return (
    <div className="popular-live-streaming" onClick={() => popularLiveClick()}>
      <div className="top-title">热门直播</div>

      {loadStatus === LoadStatus.SUCCESS ? (
        <div className="live-streaming">
          <div className="img">{courseImg && <img src={courseImg} alt="" onClick={setCourseImgClick} />}</div>

          <div className="right">
            {dataList.courseList.map((item) => (
              <Item key={item.id} {...item} setCourseImg={setCourseImg} />
            ))}
          </div>
        </div>
      ) : (
        <div className="live-streaming-loading">
          <img
            className="loading-icon"
            src="//s3plus-img.meituan.net/v1/mss_a15b7cd66e264e09b68e65865d5f8e2b/mtdx/mtdx-loading.gif"
            alt="loading"
          />
        </div>
      )}
    </div>
  );
};

export default PopularLiveStreaming;
